import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TuiAlertService } from '@taiga-ui/core';
import { Observable, Subject, tap, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ObjectCardKind } from '@common/components/object-card';
import { AccessService, getKeysFromBinary } from '@common/services/access.service';
import { FileService } from '@common/services/file.service';
import { ChangeObjectFromSocket } from '@services/websocket.service';

import { ShortMaintenanceIntervalsList } from '../app/portal/components/register-events/register-events.models';
import {
  CommandFullInfo,
  DriverShortInfo,
  SensorShortInfo,
  TrailerShortInfo,
} from '../app/portal/portal.types';

@Injectable({
  providedIn: 'root',
})
export class ObjectsService {
  private readonly httpClient = inject(HttpClient);
  private readonly alerts = inject<TuiAlertService>(TuiAlertService);
  private readonly translate = inject(TranslateService);
  private readonly accessService = inject(AccessService);
  private readonly fileService = inject(FileService);

  changeObjectFromSocket$ = new Subject<ChangeObjectFromSocket>();

  create(form: any) {
    return this.httpClient.post('objects/', form);
  }

  update(form: any, objectId: any) {
    return this.httpClient.put(`objects/${objectId}/`, form);
  }

  objectsList(params: any = null) {
    let httpParams = new HttpParams();
    if (params?.fields?.length) {
      httpParams = httpParams.set('fields', params.fields.toString());
    }

    return this.httpClient.get('objects/', { params: httpParams }).pipe(
      map((list: any) => {
        return list.map((item: any) => ({
          ...item,
          currentUserAccess:
            typeof item.currentUserAccess === 'number'
              ? getKeysFromBinary(
                  this.accessService.allAccessRights.objects,
                  item.currentUserAccess,
                )
              : item.currentUserAccess,
        }));
      }),
    );
  }

  shortList() {
    return this.httpClient.get('objects/short/');
  }

  linkingObjectsByObjectId(objectId: number) {
    let httpParams = new HttpParams();

    httpParams = httpParams.set('object_id', objectId.toString());

    return this.httpClient.get(`object_user_access/`, { params: httpParams });
  }

  /**
   * @param card — вернуть данные в формате карточки объекта (`?card=true`)
   * @param cardType — тип карточки; бэкенд отдаёт только блоки, включённые в настройках этого типа
   */
  getObjectById(objectId: number, full = true, card = false, cardType?: ObjectCardKind) {
    let params = new HttpParams();
    if (full) {
      params = params.set('full', full);
    }

    if (card) {
      params = params.set('card', card);

      if (cardType) {
        params = params.set('type', cardType);
      }
    }

    return this.httpClient.get(`objects/${objectId}/`, { params }).pipe(
      map((res: any) => {
        return {
          ...res,
          currentUserAccess:
            typeof res.currentUserAccess === 'number'
              ? getKeysFromBinary(this.accessService.allAccessRights.objects, res.currentUserAccess)
              : res.currentUserAccess,
        };
      }),
    );
  }

  getSpecificationList(accountId?: number): Observable<any> {
    return this.httpClient.get<any>(`objects/specification_list/`);
  }

  getShortSensorsListByObjectId(objectId: number): Observable<SensorShortInfo[]> {
    return this.httpClient.get<SensorShortInfo[]>(`objects/${objectId}/sensors/`);
  }

  getCurrentValueSensorById(sensorId: number): Observable<number | null> {
    return this.httpClient
      .get<{
        currentValue: number | null;
        timestamp: string;
        unit: string;
      }>(`maintenance/sensor-value/${sensorId}/`)
      .pipe(map((res) => res.currentValue));
  }

  getShortMaintenanceListByObjectId(objectId: number): Observable<{
    list: ShortMaintenanceIntervalsList[];
    mileageSensorId: number | null;
    engineSensorId: number | null;
  }> {
    let params = new HttpParams();
    params = params.set('maintenance', true);

    return this.httpClient
      .get<{
        maintenance: {
          list: ShortMaintenanceIntervalsList[];
          mileageSensorId: number | null;
          engineSensorId: number | null;
        };
      }>(`objects/${objectId}/`, { params })
      .pipe(
        map(({ maintenance }) => ({
          list: maintenance.list,
          engineSensorId: maintenance?.engineSensorId || null,
          mileageSensorId: maintenance?.mileageSensorId || null,
        })),
      );
  }

  getFullCommandsListByObjectId(objectId: number): Observable<CommandFullInfo[]> {
    return this.httpClient.get<CommandFullInfo[]>(`objects/${objectId}/commands/`);
  }

  getShortDriversListByObjectId(objectId: number): Observable<DriverShortInfo[]> {
    return this.httpClient.get<DriverShortInfo[]>(`objects/${objectId}/drivers/`);
  }

  getShortTrailersListByObjectId(objectId: number): Observable<TrailerShortInfo[]> {
    return this.httpClient.get<DriverShortInfo[]>(`objects/${objectId}/trailers/`);
  }

  getAvailableParamsById(objectId: number) {
    return this.httpClient.get(`objects/available_params/${objectId}/`);
  }

  getAvailableParamsByIds(objectIds: number[]) {
    return this.httpClient.post('objects/available_params/', { objectIds });
  }

  changeDisplayObjectsByIds(visibleObjectsIds: number[]) {
    return this.httpClient.post('objects/display/', { visibleObjectsIds });
  }

  setMapVisibility(objectIds: number[], state: boolean) {
    return this.httpClient.post('objects/set_map_visibility/', { objectIds, state });
  }

  changeCurrentValueForCalculatedSensors(objectId: number, sensorId: number, newCurrentValue: any) {
    return this.httpClient.post(`objects/${objectId}/sensors/${sensorId}/set_current_value/`, {
      newCurrentValue,
    });
  }

  deleteObject(objectId: number) {
    return this.httpClient.delete(`objects/${objectId}/`);
  }

  alert(name: string, action: string) {
    return this.alerts.open(
      this.translate.instant(action, {
        name,
        context: this.translate.instant('Object'),
      }),
    );
  }

  sendCommand(objectId: number, commandId: number): Observable<void> {
    return this.httpClient.post<void>(`objects/send_command/`, { objectId, commandId });
  }

  sendCustomCommand(objectId: number, params: string): Observable<void> {
    return this.httpClient.post<void>(`objects/${objectId}/send_command/`, { params });
  }

  registerEventForObjectId(objectId: number, body: any) {
    return this.httpClient.post(`objects/${objectId}/events/`, body);
  }

  deleteEventForObjectId(objectId: number, body: { eventIds: number[] }) {
    return this.httpClient.post(`objects/${objectId}/events/bulk_delete/`, body);
  }

  deleteMessagesForObjectId(objectId: number, body: [string, number][]) {
    return this.httpClient.post(`objects/${objectId}/messages/bulk_delete/`, body);
  }

  partialImport(payload: any): Observable<any> {
    return this.httpClient.post<any>(`objects/axenta_partial_import/`, payload);
  }

  // TODO: refactor
  exportObjectsAndSave(body: any): Observable<Blob> {
    return this.httpClient
      .post(`objects/export/`, body, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        tap((response) => {
          const contentDisposition = response.headers.get('Content-Disposition');

          const { fileName, fileFormat } = this.fileService.extractFileNameAndFormat(
            contentDisposition,
            'objects_export',
            'alp',
          );
          this.fileService.downloadFile(response.body!, fileName, fileFormat);
        }),
        catchError((error: any) => {
          if (error.type === 'application/json') {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const errorData = JSON.parse(reader.result as string);
                if (errorData?.detail?.length) {
                  const messages = errorData?.detail;
                  messages.forEach((message: string) => {
                    this.alerts.open(message, { appearance: 'error', autoClose: 6000 }).subscribe();
                  });
                }
              } catch (e) {
                console.error('Ошибка при разборе JSON:', e);
              }
            };

            return throwError(() => reader.readAsText(error));
          }

          return throwError(() => error);
        }),
        map((response) => response.body!),
      );
  }
}
